import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { getSyncConfig, setSyncConfig, getLastSyncAt } from '../../sync/config';
import { syncNow } from '../../sync/syncClient';
import { exerciseMap, finishedSessions } from '../../db/repo';
import { sessionsToCSV, sessionsToMarkdown } from '../../sync/format';
import { downloadText } from '../../lib/download';
import { validateImport, importHistory, type ImportResult } from './importHistory';
import { IMPORT_PROMPT } from './importPrompt';
import { Toast } from '../../ui/primitives';
import { IconBack } from '../../ui/icons';
import { todayISO, timeAgoLabel } from '../../lib/dates';

export function SettingsScreen() {
  const navigate = useNavigate();
  const initial = getSyncConfig();
  const [workerUrl, setWorkerUrl] = useState(initial.workerUrl);
  const [secret, setSecret] = useState(initial.secret);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const lastSync = useLiveQuery(() => getLastSyncAt(), []);

  async function saveSync() {
    await setSyncConfig({ workerUrl, secret });
    setToast('Saved.');
  }

  async function testSync() {
    await setSyncConfig({ workerUrl, secret });
    setBusy(true);
    const { flush, measurements, pull } = await syncNow();
    setBusy(false);
    if (!flush.configured) { setToast('Fill in Worker URL + secret first.'); return; }
    if (flush.error) { setToast(`Sync error: ${flush.error}`); return; }
    const meas = measurements.synced ? `, measurements ${measurements.synced}` : '';
    setToast(`Synced ${flush.synced}${meas}, pulled coaching ${pull.applied}.`);
  }

  async function exportCsv() {
    const [sessions, exById] = await Promise.all([finishedSessions(), exerciseMap()]);
    downloadText(`gym-export-${todayISO()}.csv`, sessionsToCSV(sessions, exById), 'text/csv');
  }
  async function exportMd() {
    const [sessions, exById] = await Promise.all([finishedSessions(), exerciseMap()]);
    downloadText(`gym-export-${todayISO()}.md`, sessionsToMarkdown(sessions, exById), 'text/markdown');
  }

  async function doImport() {
    const { data, error } = validateImport(importText);
    if (error || !data) { setToast(error ?? 'Invalid import.'); return; }
    setBusy(true);
    const exById = await exerciseMap();
    const result = await importHistory(data, exById);
    setBusy(false);
    setImportResult(result);
    setToast(`Imported ${result.importedSessions} sessions.`);
  }

  function copyPrompt() {
    void navigator.clipboard?.writeText(IMPORT_PROMPT).then(() => setToast('Import prompt copied.'));
  }

  return (
    <div className="screen">
      <button className="btn ghost sm" onClick={() => navigate('/more')} style={{ marginBottom: 10 }}>
        <IconBack width={18} height={18} /> More
      </button>
      <div className="screen-title" style={{ marginBottom: 4 }}>Sync, import & export</div>

      <div className="section-label">Notion sync (optional)</div>
      <div className="info-banner">
        The app is fully usable offline without this. To enable the Notion mirror + Cowork coaching, deploy the
        Cloudflare Worker (see README) and paste its URL + shared secret here.
      </div>
      <div className="card">
        <div className="field">
          <label>Worker URL</label>
          <input className="input" placeholder="https://gym-sync.your-name.workers.dev" value={workerUrl} onChange={(e) => setWorkerUrl(e.target.value)} autoCapitalize="off" autoCorrect="off" />
        </div>
        <div className="field">
          <label>Shared secret</label>
          <input className="input" type="password" placeholder="the SYNC_SECRET you set on the Worker" value={secret} onChange={(e) => setSecret(e.target.value)} />
        </div>
        <div className="row" style={{ gap: 10 }}>
          <button className="btn grow" onClick={saveSync}>Save</button>
          <button className="btn primary grow" onClick={testSync} disabled={busy}>{busy ? 'Working' : 'Save & sync now'}</button>
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 10 }}>
          {lastSync ? `Last synced ${timeAgoLabel(lastSync)}` : 'Not synced yet'}
        </div>
      </div>

      <div className="section-label">Import Notes history (one-time)</div>
      <div className="info-banner">
        Paste your Apple Notes training log into Claude/Cowork with the prompt below, then paste the JSON it returns here.
        Unmatched exercises are reported, never invented.
      </div>
      <div className="card">
        <button className="btn ghost full" onClick={copyPrompt} style={{ marginBottom: 10 }}>Copy the import prompt</button>
        <textarea className="textarea" placeholder='{"sessions":[ ... ]}' value={importText} onChange={(e) => setImportText(e.target.value)} />
        <button className="btn primary full" style={{ marginTop: 8 }} onClick={doImport} disabled={busy || !importText.trim()}>Import</button>
        {importResult && (
          <div className="info-banner" style={{ marginTop: 10 }}>
            Imported {importResult.importedSessions} sessions, {importResult.importedEntries} exercises.
            {importResult.warnings.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {importResult.warnings.slice(0, 8).map((w, i) => <li key={i} style={{ fontSize: 12 }}>{w}</li>)}
                {importResult.warnings.length > 8 && <li style={{ fontSize: 12 }}>...and {importResult.warnings.length - 8} more</li>}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="section-label">Export</div>
      <div className="card">
        <div className="row" style={{ gap: 10 }}>
          <button className="btn grow" onClick={exportCsv}>Export CSV</button>
          <button className="btn grow" onClick={exportMd}>Export Markdown</button>
        </div>
      </div>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
